import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { InitiativeListItem } from '@complan/contracts';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { StatusBadge } from '@/components/ui/status-badge';
import { isPlainRowActivation } from '@/shared/components/row-activation';
import { FF_ROLE_LABEL_KEYS } from '@/shared/i18n/enum-labels';
import type { PlatformOption } from './initiative-list-data';

/**
 * The Initiative list table (UI spec 3.4's table conventions: a header row of
 * 10.5px uppercase muted labels, one row per Initiative, multi-value associations
 * as tags).
 *
 * The identifier column is the sortable one and the only one: this screen sorts by
 * Initiative identifier, which the endpoint supports directly as
 * `sortField=initiative_ref`, and `aria-sort` on that header is what tells a screen
 * reader both that the column is sorted and in which direction. The remaining
 * columns are presentational, so they carry no sort affordance that would imply
 * one.
 *
 * ## The three demand-hours columns are absent, deliberately
 *
 * The click dummy's list carries `ADE h`, `BDE h` and `Total h`. Those are mock
 * numbers: nothing in ComPlan stores the drivers they are computed from.
 * `initiative_ff_scope` holds a role, an activity type, an audience, a material
 * flag, a work order and an information contribution - and no coverage factor, no
 * frequency, no POS count, no effort per unit and no fixed overhead - which is
 * exactly what R5.2 specifies and no more. UI spec 0.1 places the derived-hours
 * calculation in the deferred manpower engine, and its section 5 screens (Effort
 * Matrix, Capacity Inputs) are the ones that would hold the missing inputs.
 * Rendering the columns would mean either inventing the model or printing three
 * permanently empty cells, so they are left out until the engine exists.
 */
export interface InitiativeListTableProps {
  readonly items: readonly InitiativeListItem[];
  readonly platforms: readonly PlatformOption[];
  /** Resolves a Sales_Cycle id to its name, or `undefined` when unresolvable. */
  readonly salesCycleLabel: (id: string) => string | undefined;
  readonly sortDirection: 'asc' | 'desc';
  readonly onToggleSort: () => void;
  /**
   * Bulk_Edit selection (`complan-governance` R10.4). Absent for a reader who cannot
   * write, so no checkbox column renders at all - a disabled one would advertise an
   * operation they cannot perform.
   */
  readonly selection?: {
    readonly selectedIds: ReadonlySet<string>;
    readonly onToggle: (initiativeId: string) => void;
    /** Selects or clears every row on this page. */
    readonly onToggleAll: () => void;
  };
}

export function InitiativeListTable({
  items,
  platforms,
  salesCycleLabel,
  sortDirection,
  onToggleSort,
  selection,
}: InitiativeListTableProps): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // The list's own view state, carried into the panel route so the parent keeps it.
  const [search] = useSearchParams();
  const query = search.toString();
  const platformById = new Map(platforms.map((platform) => [platform.id, platform]));

  // Select-all reflects only *this page*: the reader can see these rows and R10.4's
  // preview is about records they chose, so "all" can never mean rows they never saw.
  const pageIds = items.map((item) => item.id);
  const selectedOnPage = pageIds.filter((id) => selection?.selectedIds.has(id) === true).length;
  const allOnPageSelected = pageIds.length > 0 && selectedOnPage === pageIds.length;

  return (
    <Table>
      <caption className="sr-only">{t('initiativeList.tableCaption')}</caption>
      <TableHeader>
        <TableRow>
          {selection === undefined ? null : (
            <TableHead scope="col" className="w-8">
              <input
                type="checkbox"
                data-testid="bulk-select-all"
                checked={allOnPageSelected}
                // Some but not all: the box shows neither checked nor empty, because both
                // would misstate what pressing it will do.
                ref={(node) => {
                  if (node !== null) {
                    node.indeterminate = selectedOnPage > 0 && !allOnPageSelected;
                  }
                }}
                onChange={selection.onToggleAll}
                aria-label={t('initiativeList.selectAllOnPage')}
              />
            </TableHead>
          )}
          <TableHead
            scope="col"
            className="text-[10.5px] uppercase"
            aria-sort={sortDirection === 'asc' ? 'ascending' : 'descending'}
          >
            <button
              type="button"
              onClick={onToggleSort}
              className="focus-visible:border-accent-line -mx-1 flex items-center gap-1 rounded-[var(--radius-control)] px-1 uppercase outline-none"
            >
              {t('initiativeList.colId')}
              <span aria-hidden="true" className="opacity-60">
                {sortDirection === 'asc' ? '▲' : '▼'}
              </span>
            </button>
          </TableHead>
          <TableHead scope="col" className="text-[10.5px] uppercase">
            {t('initiativeList.colTitle')}
          </TableHead>
          <TableHead scope="col" className="text-[10.5px] uppercase">
            {t('initiativeList.colPlatform')}
          </TableHead>
          <TableHead scope="col" className="text-[10.5px] uppercase">
            {t('initiativeList.colSalesCycle')}
          </TableHead>
          <TableHead scope="col" className="text-[10.5px] uppercase">
            {t('initiativeList.colFieldForceRole')}
          </TableHead>
          <TableHead scope="col" className="text-[10.5px] uppercase">
            {t('initiativeList.colStatus')}
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow
            key={item.id}
            onClick={(event) => openDetailPanel(event, detailPanelPath(item, query), navigate)}
            // UI spec 3.5's clickable-row treatment: `--accent-soft` on hover plus
            // a pointer, which is what distinguishes a row that opens something
            // from the Table primitive's default `hover:bg-muted/50` read-only
            // highlight. `cn` (tailwind-merge) drops that default in favour of this.
            className="hover:bg-accent-soft cursor-pointer"
            data-selected={selection?.selectedIds.has(item.id) === true ? 'true' : undefined}
          >
            {selection === undefined ? null : (
              <TableCell className="w-8">
                {/* `isPlainRowActivation` already ignores a click whose target is an
                    `input`, so ticking a row does not also open its Detail_Panel - the
                    guard the Demand Explorer's rows share, which is why no
                    `stopPropagation` is needed here. */}
                <input
                  type="checkbox"
                  data-testid="bulk-select-row"
                  checked={selection.selectedIds.has(item.id)}
                  onChange={() => selection.onToggle(item.id)}
                  aria-label={t('initiativeList.selectRow', { ref: item.initiativeRef })}
                />
              </TableCell>
            )}
            <TableCell className="font-mono text-[11.5px]">
              {/* Opens the Detail_Panel over this list, as a nested route.

                  Renders as plain text, not as a link: the whole row opens the same
                  target, so an underlined accent identifier advertised a second,
                  narrower affordance for something the reader can already click
                  anywhere along. Only the styling goes, though — the anchor itself
                  stays, because it is the *only* keyboard route into the panel. The
                  row is a `<tr>`, which is not focusable and carries no role that
                  would make its click handler reachable by keyboard; the handler is
                  a pointer convenience layered over this element, never a
                  replacement for it. Removing the anchor too would leave the panel
                  openable by mouse alone (R18.6) and would also drop the browser's
                  own open-in-new-tab and copy-address behaviour.

                  Tailwind's preflight sets `a { color: inherit; text-decoration:
                  inherit }`, so dropping the hover classes is all it takes to make
                  this read as the cell's own text. The focus ring is kept explicit
                  because it is now the only thing that reveals the element to a
                  keyboard reader. */}
              <Link
                to={detailPanelPath(item, query)}
                className="focus-visible:outline-accent-line rounded-[var(--radius-control)] focus-visible:outline-2 focus-visible:outline-offset-1"
              >
                {item.initiativeRef}
              </Link>
            </TableCell>
            <TableCell className="max-w-[380px] truncate text-[12px]" title={item.title}>
              {item.title}
            </TableCell>
            <TableCell>
              <PlatformTags item={item} platformById={platformById} />
            </TableCell>
            <TableCell>
              <TagSet
                ids={item.salesCycleIds}
                label={salesCycleLabel}
                emptyLabel={t('initiativeList.noSalesCycle')}
              />
            </TableCell>
            <TableCell>
              <FieldForceRoleTags roles={item.fieldForceRoles} />
            </TableCell>
            <TableCell>
              <StatusBadge status={item.status} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * The Detail_Panel route for an Initiative, nested under this list
 * (`/initiatives/:id`) so the panel opens *here* rather than sending the reader to
 * the calendar. Shared by the identifier link and the row handler so the two can
 * never drift to different targets.
 *
 * The current query string is carried over deliberately. The list keeps its
 * filters, search, sort and page window in the search params, and the parent route
 * stays mounted while the panel is open, so dropping the query here would reset the
 * list underneath the panel and land the reader on an unfiltered first page when
 * they closed it (R11.13).
 */
function detailPanelPath(item: InitiativeListItem, query: string): string {
  return `/initiatives/${item.id}${query === '' ? '' : `?${query}`}`;
}

/**
 * Opens the Detail_Panel for a row click, unless the click was the browser's or a
 * nested control's — see {@link isPlainRowActivation}, which the Demand Explorer's
 * initiative rows share so both tables treat modified clicks, in-cell links and
 * text selections the same way.
 */
function openDetailPanel(
  event: React.MouseEvent<HTMLTableRowElement>,
  path: string,
  navigate: (to: string) => void,
): void {
  if (isPlainRowActivation(event)) {
    navigate(path);
  }
}

/**
 * The Platform cell: the primary Platform first, then any additional ones.
 *
 * The primary is marked, because "primary Platform" is a governed attribute
 * (GV-002 raises a finding when it is missing) rather than a presentation detail,
 * and a flat list of tags would lose which one it is.
 *
 * Each tag shows the Platform's display name, not its `code` — the lowercase
 * internal identifier master data stores for export round-tripping and
 * uniqueness. Rendering `code` would surface that internal identifier (and
 * whatever casing an administrator happened to type) as if it were the display
 * value.
 */
function PlatformTags({
  item,
  platformById,
}: {
  readonly item: InitiativeListItem;
  readonly platformById: ReadonlyMap<string, PlatformOption>;
}): React.JSX.Element {
  const { t } = useTranslation();
  // An unresolved Platform renders as an explicit marker, never as its raw id -
  // see `labelLookup` in `initiative-list-data.ts` for why a UUID in this
  // position is worse than an admitted gap. The id stays reachable as the
  // tooltip, which is where it is useful without reading as the display value.
  const name = (id: string): string =>
    platformById.get(id)?.label ?? t('initiativeList.unresolvedLabel');
  const isResolved = (id: string): boolean => platformById.has(id);
  const tooltip = (id: string): string =>
    isResolved(id) ? name(id) : t('initiativeList.unresolvedLabelTitle', { id });
  const colorHex = (id: string): string | null => platformById.get(id)?.colorHex ?? null;

  return (
    <span className="flex flex-wrap gap-1">
      <Badge
        variant="outline"
        style={platformTagStyle(colorHex(item.primaryPlatformId))}
        title={
          isResolved(item.primaryPlatformId)
            ? t('initiativeList.primaryPlatform', { name: name(item.primaryPlatformId) })
            : tooltip(item.primaryPlatformId)
        }
      >
        {name(item.primaryPlatformId)}
      </Badge>
      {item.additionalPlatformIds.map((id) => (
        <Badge key={id} variant="ghost" style={platformTagStyle(colorHex(id))} title={tooltip(id)}>
          {name(id)}
        </Badge>
      ))}
    </span>
  );
}

/**
 * The inline style that renders a Platform tag in its configured colour (R16.1),
 * the same colour the Calendar_View uses for that Platform's bars and legend, so
 * an Initiative reads consistently across screens.
 *
 * The colour drives a tinted background, a full-strength border and full-strength
 * text: the border and text carry the identity at readable contrast while the
 * low-alpha fill keeps a row of tags from becoming a wall of saturated blocks.
 * `#rrggbb` is stored (migration 0002's `platform_color_hex_format` CHECK), so an
 * 8-digit `#rrggbbaa` suffix is a safe way to derive the fill without a colour
 * library. A Platform with no colour set (`null`) falls back to the Badge
 * variant's own neutral styling by returning no overrides.
 */
function platformTagStyle(colorHex: string | null): React.CSSProperties | undefined {
  if (colorHex === null) {
    return undefined;
  }
  return {
    backgroundColor: `${colorHex}1f`,
    borderColor: colorHex,
    color: colorHex,
  };
}

/** The Field Force role tags (`t-ade`/`t-bde` of UI spec 3.4); empty means no involvement. */
function FieldForceRoleTags({
  roles,
}: {
  readonly roles: InitiativeListItem['fieldForceRoles'];
}): React.JSX.Element {
  const { t } = useTranslation();
  if (roles.length === 0) {
    return <span className="text-muted-foreground text-[11.5px]">{t('initiativeList.noFieldForce')}</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {roles.map((role) => (
        <Badge key={role} variant="secondary">
          {t(FF_ROLE_LABEL_KEYS[role])}
        </Badge>
      ))}
    </span>
  );
}

/**
 * A set of ids rendered as tags, with an explicit label when the set is empty and
 * an explicit marker for any id the lookup could not resolve (never the raw id -
 * see `labelLookup`).
 */
function TagSet({
  ids,
  label,
  emptyLabel,
}: {
  readonly ids: readonly string[];
  readonly label: (id: string) => string | undefined;
  readonly emptyLabel: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  if (ids.length === 0) {
    return <span className="text-muted-foreground text-[11.5px]">{emptyLabel}</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {ids.map((id) => {
        const resolved = label(id);
        return (
          <Badge
            key={id}
            variant="secondary"
            title={resolved ?? t('initiativeList.unresolvedLabelTitle', { id })}
          >
            {resolved ?? t('initiativeList.unresolvedLabel')}
          </Badge>
        );
      })}
    </span>
  );
}
